import React, { useState } from "react";
import styled from "styled-components";
import { Col, Row } from "reactstrap";
import { OutputRow } from "../../../utils/budget-tool-specific";
import { RebelColors, RebelButton } from "../../../styles/rebel-experiences";
import { displayValue } from "../helper";
import { calculateVillaTotalPerPerson } from "./rebel-budget-property-tab";
import TallyDisplay from "../rebel-experiences/components/tally-display";
import RankingDropdown from "../rebel-experiences/components/ranking-dropdown";
import { updateVoterCookie } from "../../../utils/ranking-cookies";
import { updateDocumentProperty } from "../../../utils/firestore-functions";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_xiqdcy8";
const EMAILJS_RANKING_TEMPLATE_ID = "template_rebel_ranking"; // Update after creating in EmailJS dashboard
const EMAILJS_PUBLIC_KEY = "user_8Qt3ZJv9dlnGhiJefkhBh";

const RebelBudgetSummary = ({
    properties,
    villaData,
    categories,
    config,
    tripLength,
    currencySymbol,
    allRankings,
    selectedRankings,
    onRankingChange,
    savedRankings,
    setSavedRankings,
    voterId,
    budgetId,
    budgetDB,
    savedEmail,
    savedContactName,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const attendees = config.attendees || 1;
    const totalProperties = properties.length;
    const currencyCode = config.currency ? config.currency.slice(0, 3) : "USD";

    // Check if all properties are ranked
    const isComplete = () => {
        return properties.length > 0 &&
            Object.keys(selectedRankings).length === properties.length &&
            Object.values(selectedRankings).every(rank => rank !== null && rank !== undefined);
    };

    // Check for unsaved changes
    const hasUnsavedChanges = () => {
        const savedKeys = Object.keys(savedRankings).sort();
        const selectedKeys = Object.keys(selectedRankings).sort();
        if (savedKeys.length !== selectedKeys.length) return true;
        return selectedKeys.some(key => savedRankings[key] !== selectedRankings[key]);
    };

    // Send ranking notification to POC (#12)
    const sendRankingNotification = async (voterName) => {
        if (!savedEmail) return; // No POC email available

        try {
            // Build rankings summary
            const rankingSummary = properties.map(villaId => {
                const villa = villaData[villaId];
                const name = villa?.villaName || villa?.name || "Unknown";
                const rank = selectedRankings[villaId];
                return `${rank !== 'N/A' ? `#${rank}` : 'N/A'} - ${name}`;
            }).join('\n');

            await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_RANKING_TEMPLATE_ID, {
                to_email: savedEmail,
                to_name: savedContactName || "Team Lead",
                voter_name: voterName || "A team member",
                company_name: config.companyName || "Your Team",
                ranking_summary: rankingSummary,
                num_properties: properties.length,
                generated_date: new Date().toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                }),
            }, EMAILJS_PUBLIC_KEY);
        } catch (error) {
            console.warn("Failed to send ranking notification:", error);
        }
    };

    // Submit rankings
    const handleSubmitRankings = async () => {
        if (!isComplete()) {
            alert("Please rank all properties before submitting.");
            return;
        }

        if (!voterId) {
            alert("Unable to submit rankings. Please refresh the page and try again.");
            return;
        }

        setIsSubmitting(true);

        const cookieId = budgetId || "rebel-budget-new";
        const timestamp = new Date().getTime();

        try {
            // If we have a saved budget, persist rankings to database
            if (budgetId) {
                await updateDocumentProperty(
                    budgetDB,
                    "rebelClientBudgets",
                    budgetId,
                    `rankings.${voterId}`,
                    { timestamp, votes: selectedRankings },
                    () => {
                        updateVoterCookie(cookieId, voterId, selectedRankings);
                        setSavedRankings(selectedRankings);

                        // Send notification to POC (#12)
                        sendRankingNotification(voterId);

                        alert("Rankings submitted successfully!");
                        setIsSubmitting(false);
                    },
                    (error) => {
                        console.error("Error submitting rankings:", error);
                        alert("Failed to submit rankings. Please try again.");
                        setIsSubmitting(false);
                    }
                );
            } else {
                // No saved budget yet - just save locally via cookie
                updateVoterCookie(cookieId, voterId, selectedRankings);
                setSavedRankings(selectedRankings);
                alert("Rankings saved locally! Save your budget to share with others.");
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error("Error submitting rankings:", error);
            alert("Failed to submit rankings. Please try again.");
            setIsSubmitting(false);
        }
    };

    const showSubmitButton = totalProperties > 0;
    const isButtonDisabled = !isComplete() || !hasUnsavedChanges() || isSubmitting;

    if (properties.length === 0) {
        return (
            <EmptyState>
                <EmptyTitle>No Properties Added</EmptyTitle>
                <EmptyText>Click the "+" tab above to add properties to compare.</EmptyText>
            </EmptyState>
        );
    }

    return (
        <div>
            {/* Header row */}
            <SummaryHeaderRow>
                <TallyHeaderCol lg="1" xs="1">
                    <BoldLabel>Tally</BoldLabel>
                </TallyHeaderCol>
                <Col>
                    <BoldLabel>Property</BoldLabel>
                </Col>
                <Col lg="2">
                    <BoldLabel>Cost Per Attendee</BoldLabel>
                </Col>
                <Col lg="2">
                    <BoldLabel>Est. Total</BoldLabel>
                </Col>
                <RankingHeaderCol lg="2" xs="2">
                    <BoldLabel>Ranking</BoldLabel>
                </RankingHeaderCol>
            </SummaryHeaderRow>

            {/* Property rows */}
            {properties.map(villaId => {
                const villa = villaData[villaId];
                const name = villa?.villaName || villa?.name || "Loading...";
                const totalPerPerson = villa ? calculateVillaTotalPerPerson(villa, categories, tripLength, config) : 0;
                const totalTrip = totalPerPerson * attendees;

                return (
                    <PropertyRow key={villaId}>
                        <TallyCol lg="1" xs="1">
                            <TallyDisplay
                                allRankings={allRankings}
                                experienceId={villaId}
                                displayMode="average"
                            />
                        </TallyCol>
                        <TextCol>
                            <PropertyText>{name}</PropertyText>
                            {villa?.location && <LocationText>{villa.location}</LocationText>}
                        </TextCol>
                        <TextCol lg="2">
                            <CostText>{currencySymbol}{displayValue(totalPerPerson)} {currencyCode}</CostText>
                        </TextCol>
                        <TextCol lg="2">
                            <CostText>{currencySymbol}{displayValue(totalTrip)} {currencyCode}</CostText>
                        </TextCol>
                        <RankingCol lg="2" xs="2">
                            <RankingDropdown
                                experienceId={villaId}
                                selectedRankings={selectedRankings}
                                onChange={onRankingChange}
                                totalExperiences={totalProperties}
                            />
                        </RankingCol>
                    </PropertyRow>
                );
            })}

            {/* Submit button */}
            {showSubmitButton && (
                <SubmitButtonContainer>
                    <SubmitButton
                        onClick={handleSubmitRankings}
                        disabled={isButtonDisabled}
                    >
                        {isSubmitting ? 'Submitting...' :
                         Object.keys(savedRankings).length > 0 ? 'Update Rankings' : 'Submit Rankings'}
                    </SubmitButton>
                </SubmitButtonContainer>
            )}
        </div>
    );
};

export default RebelBudgetSummary;

const EmptyState = styled.div`
    text-align: center;
    padding: 3rem 1.5rem;
`;

const EmptyTitle = styled.h3`
    color: ${RebelColors.text};
    margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 1rem;
`;

const SummaryHeaderRow = styled(Row)`
    border-bottom: 2px solid rgba(253, 181, 6, 0.4);
    padding-bottom: 8px;
    margin-bottom: 4px;
    display: flex;
    align-items: flex-end;
`;

const PropertyRow = styled(Row)`
    border-bottom: 1px solid ${RebelColors.border};
    padding: 8px 0;
    display: flex;
    align-items: center;
`;

const BoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    margin-bottom: 0;
    font-size: 0.9rem;
`;

const TallyHeaderCol = styled(Col)`
    text-align: center;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const RankingHeaderCol = styled(Col)`
    text-align: center;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const TallyCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const TextCol = styled(Col)`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const RankingCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 5px !important;
    padding-right: 5px !important;
    overflow: visible;
`;

const PropertyText = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-weight: 500;
`;

const LocationText = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.8rem;
`;

const CostText = styled.p`
    color: ${RebelColors.text};
    margin: 0;
`;

const SubmitButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid ${RebelColors.border};
`;

const SubmitButton = styled(RebelButton)`
    width: 100%;
    max-width: 300px;
    padding: 12px 24px;
    font-size: 1rem;
    font-weight: 600;

    &:not(:disabled) {
        border: 2px solid ${RebelColors.gold};
        box-shadow: 0 0 8px rgba(253, 181, 6, 0.3);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background-color: ${RebelColors.border};
        border-color: ${RebelColors.border};
    }
`;
